import { useState, useRef, useEffect, useCallback, KeyboardEvent, ChangeEvent, DragEvent } from 'react';
import { Send, Mic, Radio, Paperclip, X, AlertCircle } from 'lucide-react';
import ModeToggle, { type ConversationMode } from './ModeToggle';
import { validateAttachmentBatch, ACCEPTED_FILE_ACCEPT_ATTR } from '../utils/attachmentValidation';
import DreamDevLogo from './DreamDevLogo';
import ArcPilotLogo from './ArcPilotLogo';

interface CenteredInputProps {
  mode: ConversationMode;
  onModeChange: (mode: ConversationMode) => void;
  onSubmit: (message: string, files?: File[]) => void;
  onStartVoice: () => void;
  isLoading?: boolean;
}

const placeholders: Record<Exclude<ConversationMode, 'watch'>, string> = {
  // Brain-dump framing (FR-013 / spec §4): invite an unstructured first pass in
  // the SME's own words, not an interview-style "describe the process to map".
  discovery:
    "Walk me through this process from start to finish, in your own words — don't worry about order or gaps, we'll tidy it up together.",
  infer: "What do you want to build? I'll generate a starting point...",
};

const starterChips: Record<Exclude<ConversationMode, 'watch'>, string[]> = {
  discovery: [
    'Map an employee onboarding process',
    'Design a payment workflow',
    'Document our support ticket flow',
  ],
  infer: [
    'Build an expense management app',
    'Design a SaaS billing system',
    'Create a CRM workflow',
  ],
};

export default function CenteredInput({
  mode,
  onModeChange,
  onSubmit,
  onStartVoice,
  isLoading = false,
}: CenteredInputProps) {
  const [value, setValue] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [attachmentErrors, setAttachmentErrors] = useState<string[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, []);

  useEffect(() => {
    autoResize();
  }, [value, autoResize]);

  const handleSubmit = () => {
    const trimmed = value.trim();
    if ((!trimmed && files.length === 0) || isLoading) return;
    onSubmit(trimmed, files);
    setValue('');
    setFiles([]);
    setAttachmentErrors([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
  };

  const handleChipClick = (chip: string) => {
    if (!isLoading) onSubmit(chip);
  };

  const addFiles = (incoming: FileList | File[]) => {
    const { accepted, errors } = validateAttachmentBatch(incoming);
    if (accepted.length > 0) setFiles((prev) => [...prev, ...accepted]);
    setAttachmentErrors(errors);
  };

  const dismissErrors = () => setAttachmentErrors([]);

  const handleFilePick = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (isLoading) return;
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
      setDragOver(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    // Only clear if leaving the outer container, not moving between children.
    if (e.currentTarget === e.target) setDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (isLoading) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="ap-centered-input">
      {/* Branding */}
      <div className="ap-centered-input__brand">
        <ArcPilotLogo variant="dark" height={72} />
        <h1 className="ap-centered-input__logo">ArcPilot</h1>
        <p className="ap-centered-input__tagline">AI-Powered Domain Discovery</p>
        <div className="ap-centered-input__by">
          <span>by</span>
          <DreamDevLogo variant="lockup" height={18} color="var(--dd-brand)" />
        </div>
      </div>

      {/* Mode toggle — prominent, centered */}
      <div className="ap-centered-input__mode-toggle">
        <ModeToggle mode={mode} onChange={onModeChange} />
      </div>

      {/* Watch mode: large start-listening button */}
      {mode === 'watch' ? (
        <button
          type="button"
          className="ap-centered-input__listen-btn"
          onClick={onStartVoice}
          disabled={isLoading}
        >
          <Radio size={24} />
          <span>Start Listening</span>
        </button>
      ) : (
        <>
          {/* Text input area — whole container is a drop-zone for files */}
          <div
            className={`ap-centered-input__box${dragOver ? ' ap-centered-input__box--drag' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {attachmentErrors.length > 0 && (
              <div className="ap-centered-input__att-errors" role="alert">
                <AlertCircle size={14} />
                <div className="ap-centered-input__att-errors-list">
                  {attachmentErrors.map((e, i) => (
                    <span key={i} className="ap-centered-input__att-error">{e}</span>
                  ))}
                </div>
                <button
                  type="button"
                  className="ap-centered-input__att-errors-close"
                  onClick={dismissErrors}
                  aria-label="Dismiss"
                >
                  <X size={12} />
                </button>
              </div>
            )}
            {files.length > 0 && (
              <div className="ap-centered-input__attachments">
                {files.map((f, i) => (
                  <span key={`${f.name}-${i}`} className="ap-centered-input__chip-att">
                    <Paperclip size={12} />
                    <span className="ap-centered-input__chip-att-name" title={f.name}>{f.name}</span>
                    <span className="ap-centered-input__chip-att-size">{formatSize(f.size)}</span>
                    <button
                      type="button"
                      className="ap-centered-input__chip-att-remove"
                      onClick={() => removeFile(i)}
                      aria-label={`Remove ${f.name}`}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <textarea
              ref={textareaRef}
              className="ap-centered-input__textarea"
              placeholder={dragOver ? 'Drop files to attach…' : placeholders[mode]}
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={isLoading}
            />
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_FILE_ACCEPT_ATTR}
              onChange={handleFilePick}
              style={{ display: 'none' }}
            />
            <div className="ap-centered-input__actions">
              <button
                type="button"
                className="ap-centered-input__btn ap-centered-input__btn--attach"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
                aria-label="Attach files"
              >
                <Paperclip size={18} />
              </button>
              <button
                type="button"
                className="ap-centered-input__btn ap-centered-input__btn--mic"
                onClick={onStartVoice}
                disabled={isLoading}
                aria-label="Start voice input"
              >
                <Mic size={18} />
              </button>
              <button
                type="button"
                className="ap-centered-input__btn ap-centered-input__btn--send"
                onClick={handleSubmit}
                disabled={isLoading || (!value.trim() && files.length === 0)}
                aria-label="Send message"
              >
                <Send size={18} />
              </button>
            </div>
          </div>

          {/* Starter chips */}
          <div className="ap-centered-input__chips">
            {starterChips[mode].map((chip) => (
              <button
                key={chip}
                type="button"
                className="ap-centered-input__chip"
                onClick={() => handleChipClick(chip)}
                disabled={isLoading}
              >
                {chip}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
